/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UserInterface } from '../../../models/interface/user.interface';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HelperService } from '../../../services/helper/helper.service';
import { filter, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { DiaryInstanceInterface } from '../../../models/interface/diary-instance.interface';
import { faHands } from '@fortawesome/free-solid-svg-icons/faHands';
import { faHandsHelping } from '@fortawesome/free-solid-svg-icons/faHandsHelping';
import { DiaryInterface } from '../../../models/interface/diary.interface';
import { BuddyStore } from '../../../store/buddy/component-store/buddy.store';
import { HttpResponse } from '@angular/common/http';
import { HelperDialogService } from 'src/app/services/helper/helper-dialog/helper-dialog.service';

export type DialogBuddyInstanceCreateData = {
  members: UserInterface[];
  interventions: InterventionInterface[];
  interventionInstances: InterventionInstanceInterface[];
  diaryInstances: DiaryInstanceInterface[];
  diaries: DiaryInterface[];
  studyId: number;
  isManager: boolean;
};

@Component({
  selector: 'app-dialog-buddy-instance-create',
  templateUrl: './dialog-buddy-instance-create.component.html',
  styleUrls: ['./dialog-buddy-instance-create.component.css'],
  providers: [BuddyStore],
})
export class DialogBuddyInstanceCreateComponent implements OnDestroy, OnInit {
  // Icons
  faHands = faHands;
  faHandsHelping = faHandsHelping;

  public members: UserInterface[];
  public membersSelection1Subject = new BehaviorSubject<UserInterface[]>([]);
  public membersSelection2Subject = new BehaviorSubject<UserInterface[]>([]);
  public interventionInstancesBuddy1: InterventionInstanceInterface[] = [];
  public interventionInstancesBuddy2: InterventionInstanceInterface[] = [];
  public diaryInstancesBuddy1: DiaryInstanceInterface[] = [];
  public diaryInstancesBuddy2: DiaryInstanceInterface[] = [];
  public interventions: InterventionInterface[] = [];
  public studyId: number;
  public diaryInstances: DiaryInstanceInterface[];
  public interventionInstances: InterventionInstanceInterface[];

  public diaries: DiaryInterface[];

  private isManager: boolean = false;

  // Selected buddies
  public selectedBuddy1: UserInterface;
  public selectedDiaryInstance1: DiaryInstanceInterface;

  public selectedBuddy2: UserInterface;
  public selectedDiaryInstance2: DiaryInstanceInterface;

  // Form submission
  public submitted = false;

  public connectBuddiesResponse$: Observable<any>;
  public createBuddyResponse = new BehaviorSubject<'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE'>('DEFAULT');

  // Filter
  public filter = { userSelection: '' };

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogBuddyInstanceCreateData,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private dialogRef: MatDialogRef<DialogBuddyInstanceCreateComponent>,
    private buddyStore: BuddyStore,
  ) {
    this.connectBuddiesResponse$ = this.buddyStore.connectBuddiesResponse$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    this.members = this.data.members;
    this.interventions = this.data.interventions;
    this.interventionInstances = this.data.interventionInstances;
    this.diaryInstances = this.data.diaryInstances;
    this.diaries = this.data.diaries;
    this.studyId = this.data.studyId;
    this.isManager = this.data.isManager;

    if (this.members.length > 0) {
      this.membersSelection1Subject.next(this.members);
      this.membersSelection2Subject.next(this.members);
      this.resetSelection1();
      this.resetSelection2();
    }
  }

  public onChangeSelection1(): void {
    if (this.selectedBuddy2) {
      this.membersSelection1Subject.next(
        this.members.filter((user) => user.id.toString() !== this.selectedBuddy2.id.toString()),
      );
    }
    if (this.selectedBuddy1) {
      this.membersSelection2Subject.next(
        this.members.filter((user) => user.id.toString() !== this.selectedBuddy1.id.toString()),
      );
    }
    this.resetSelection1();
  }

  public onChangeSelection2(): void {
    if (this.selectedBuddy2) {
      this.membersSelection1Subject.next(
        this.members.filter((user) => user.id.toString() !== this.selectedBuddy2.id.toString()),
      );
    }
    if (this.selectedBuddy1) {
      this.membersSelection2Subject.next(
        this.members.filter((user) => user.id.toString() !== this.selectedBuddy1.id.toString()),
      );
    }
    this.resetSelection2();
  }

  public isAllSelected(): boolean {
    return (
      !!this.selectedBuddy1 &&
      !!this.selectedBuddy2 &&
      !!this.selectedDiaryInstance1 &&
      !!this.selectedDiaryInstance2
    );
  }

  getInterventionInstanceOfDiaryInstance(diaryInstance: DiaryInstanceInterface) {
    if (!diaryInstance) {
      return null;
    }
    return this.interventionInstances.find(
      (instance) => instance.id == diaryInstance.attributes.intervention_instance_id,
    );
  }

  public createBuddyConnection(): void {
    this.submitted = true;
    if (this.isAllSelected() && this.createBuddyResponse.value === 'DEFAULT') {
      this.createBuddyResponse.next('LOADING');
      const interventionInstance1 = this.interventionInstances.find(
        (instance) => instance.id == this.selectedDiaryInstance1.attributes.intervention_instance_id,
      );
      const interventionInstance2 = this.interventionInstances.find(
        (instance) => instance.id == this.selectedDiaryInstance2.attributes.intervention_instance_id,
      );
      this.buddyStore.connectBuddies({
        buddyId1: this.selectedBuddy1.id,
        interventionInstanceId1: interventionInstance1.id,
        diaryInstanceId1: this.selectedDiaryInstance1.id,
        buddyId2: this.selectedBuddy2.id,
        interventionInstanceId2: interventionInstance2.id,
        diaryInstanceId2: this.selectedDiaryInstance2.id,
        studyId: this.studyId,
      });

      this.subscriptions.push(
        this.connectBuddiesResponse$
          .pipe(
            filter((value) => !!value),
            take(1),
          )
          .subscribe((result) => {
            if (result instanceof HttpResponse) {
              this.createBuddyResponse.next('SUCCESS');
              setTimeout(() => {
                this.submitted = false;
                this.dialogRef.close();
              }, 2500);
            } else {
              this.createBuddyResponse.next('FAILURE');
              setTimeout(() => {
                this.submitted = false;
                this.createBuddyResponse.next('DEFAULT');
              }, 2500);
            }
          })
      );
    } else {
      this.submitted = false;
    }
  }

  public openDialogInterventionInstanceDetails(interventionInstanceId: number): void {
    this.helperDialog
      .openDialogInterventionInstanceDetails(interventionInstanceId)
      .afterClosed()
      .subscribe(() => {});
  }

  public openDialogDiaryPreview(diaryInstance: DiaryInstanceInterface): void {
    const diary: DiaryInterface = this.helper.findArrObjById(diaryInstance.attributes.diary_id, this.diaries);
    this.helperDialog
      .openDialogDiaryPreview(diary)
      .afterClosed()
      .subscribe(() => {});
  }

  public openDialogAnswersheetPreview(diaryInstance: DiaryInstanceInterface): void {
    const diary = this.helper.findArrObjById(diaryInstance.attributes.diary_id, this.diaries);
    this.helperDialog
      .openDialogDiaryAnswersheetPreview(diaryInstance.id, diary, this.isManager)
      .afterClosed()
      .subscribe(() => {});
  }

  public resetFilter(): void {
    this.filter.userSelection = '';
    if (this.selectedBuddy2) {
      this.membersSelection1Subject.next(
        this.members.filter((user) => user.id.toString() !== this.selectedBuddy2.id.toString()),
      );
    }
    if (this.selectedBuddy1) {
      this.membersSelection2Subject.next(
        this.members.filter((user) => user.id.toString() !== this.selectedBuddy1.id.toString()),
      );
    }
  }

  private resetSelection1(): void {
    if (this.selectedBuddy1) {
      this.interventionInstancesBuddy1 = this.interventionInstances.filter(
        (instance) => this.helper.isPatientOfInstance(instance, this.selectedBuddy1.id),
      );
      this.diaryInstancesBuddy1 = this.diaryInstances.filter(
        (instance) => instance.attributes.patient_id.toString() === this.selectedBuddy1.id.toString(),
      );
    }
    this.selectedDiaryInstance1 = null;
  }

  private resetSelection2(): void {
    if (this.selectedBuddy2) {
      this.interventionInstancesBuddy2 = this.interventionInstances.filter(
        (instance) => this.helper.isPatientOfInstance(instance, this.selectedBuddy2.id),
      );
      this.diaryInstancesBuddy2 = this.diaryInstances.filter(
        (instance) => instance.attributes.patient_id.toString() === this.selectedBuddy2.id.toString(),
      );
    }
    this.selectedDiaryInstance2 = null;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
